import adminApi from './adminApi';

// ========== DTOs ==========
export interface AdminCountryDTO {
  id?: number;
  name: string;
  code?: string;
  status?: string;
}

export interface AdminCouncilDTO {
  id?: number;
  name: string;
  code?: string;
  countryId: number;
  countryName?: string;
  status?: string;
}

export interface AdminBoardDTO {
  id?: number;
  name: string;
  code?: string;
  countryId: number;
  countryName?: string;
  /** Board type for curriculum service routing: SCOTTISH, ENGLISH, INDIAN. */
  boardType?: string;
  level: string;
  regionId?: string;
  status?: string;
}

export interface AdminSchoolDTO {
  id?: number;
  name: string;
  councilId: number;
  councilName?: string;
  boardId?: number;
  boardType?: string;
  boardName?: string;
  schoolType?: string;
  status?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  postcode?: string;
}

// ========== Country API Functions ==========

export const getAllCountries = async (): Promise<AdminCountryDTO[]> => {
  const response = await adminApi.get('/api/admin/countries');
  return response.data.data;
};

export const createCountry = async (
  country: AdminCountryDTO
): Promise<AdminCountryDTO> => {
  const response = await adminApi.post('/api/admin/countries', country);
  return response.data.data;
};

export const updateCountry = async (
  id: number,
  country: AdminCountryDTO
): Promise<AdminCountryDTO> => {
  const response = await adminApi.put(`/api/admin/countries/${id}`, country);
  return response.data.data;
};

export const deactivateCountry = async (id: number): Promise<void> => {
  await adminApi.delete(`/api/admin/countries/${id}`);
};

// ========== Board API Functions ==========

export const getBoards = async (
  countryId?: number
): Promise<AdminBoardDTO[]> => {
  const params = countryId ? { countryId } : {};
  const response = await adminApi.get('/api/admin/boards', { params });
  return response.data.data;
};

export const getBoardById = async (id: number): Promise<AdminBoardDTO> => {
  const response = await adminApi.get(`/api/admin/boards/${id}`);
  return response.data.data;
};

export const createBoard = async (
  board: AdminBoardDTO
): Promise<AdminBoardDTO> => {
  const response = await adminApi.post('/api/admin/boards', board);
  return response.data.data;
};

export const updateBoard = async (
  id: number,
  board: AdminBoardDTO
): Promise<AdminBoardDTO> => {
  const response = await adminApi.put(`/api/admin/boards/${id}`, board);
  return response.data.data;
};

export const deleteBoard = async (id: number): Promise<void> => {
  await adminApi.delete(`/api/admin/boards/${id}`);
};

// ========== Council API Functions ==========

export const getCouncils = async (
  countryId?: number
): Promise<AdminCouncilDTO[]> => {
  const params = countryId ? { countryId } : {};
  const response = await adminApi.get('/api/admin/councils', { params });
  return response.data.data;
};

export const createCouncil = async (
  council: AdminCouncilDTO
): Promise<AdminCouncilDTO> => {
  const response = await adminApi.post('/api/admin/councils', council);
  return response.data.data;
};

export const updateCouncil = async (
  id: number,
  council: AdminCouncilDTO
): Promise<AdminCouncilDTO> => {
  const response = await adminApi.put(`/api/admin/councils/${id}`, council);
  return response.data.data;
};

export const deactivateCouncil = async (id: number): Promise<void> => {
  await adminApi.delete(`/api/admin/councils/${id}`);
};

// ========== School API Functions ==========

export const getSchools = async (
  councilId?: number
): Promise<AdminSchoolDTO[]> => {
  const params = councilId ? { councilId } : {};
  const response = await adminApi.get('/api/admin/schools', { params });
  return response.data.data;
};

export const createSchool = async (
  school: AdminSchoolDTO
): Promise<AdminSchoolDTO> => {
  const response = await adminApi.post('/api/admin/schools', school);
  return response.data.data;
};

export const updateSchool = async (
  id: number,
  school: AdminSchoolDTO
): Promise<AdminSchoolDTO> => {
  const response = await adminApi.put(`/api/admin/schools/${id}`, school);
  return response.data.data;
};

export const deactivateSchool = async (id: number): Promise<void> => {
  await adminApi.delete(`/api/admin/schools/${id}`);
};

